/**
 * EyeOfStreamRift - Enhanced command center shell.
 *
 * Phase 1: PriceMatrixEnhanced (clickable) + panel grid shell.
 * Panels: ArbScanner, DefensePosture, FeedTape, StalenessMonitor,
 *         Microstructure, BookDepth.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMarketStore } from '@/stores/marketStore';
import type { Sport } from '@/views/ValueDashboard/types';
import { PriceMatrixEnhanced } from './PriceMatrixEnhanced';
import { ArbScanner } from './ArbScanner';
import { DefensePosturePanel } from './DefensePosturePanel';
import { FeedTapePanel } from './FeedTapePanel';
import { StalenessMonitor } from './StalenessMonitor';
import { MicrostructurePanel } from './MicrostructurePanel';
import { BookDepthPanel } from './BookDepthPanel';

const SPORTS: Sport[] = ['nba', 'nhl', 'mlb', 'cbb', 'atp', 'wta'];

export function EyeOfStreamRift() {
  const connected = useMarketStore((s) => s.connected);
  const version = useMarketStore((s) => s.version);
  const getGames = useMarketStore((s) => s.getGames);
  const getFeedLog = useMarketStore((s) => s.getFeedLog);

  // Force dependency on getFeedLog so it's available for children
  void getFeedLog;

  const allGames = useMemo(() => {
    const games = [];
    for (const sport of SPORTS) {
      games.push(...getGames(sport, 'moneyline'));
    }
    return games;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, getGames]);

  const [selectedGameKey, setSelectedGameKey] = useState<string | null>(null);

  const selectedGame = allGames.find((g) => g.key === selectedGameKey) ?? null;

  // Declare orderbook ticker needs for every game rendered in the matrix.
  // StreamRift is the deepest panel; it reads orderbook data for depth,
  // staleness, and microstructure views, so it needs the full allGames set.
  const orderbookTickers = useMemo(() => {
    const set = new Set<string>();
    for (const g of allGames) {
      if (g.kalshi?.away.marketTicker) set.add(g.kalshi.away.marketTicker);
      if (g.kalshi?.home.marketTicker) set.add(g.kalshi.home.marketTicker);
      if (g.polymarket?.away.marketTicker) set.add(g.polymarket.away.marketTicker);
      if (g.polymarket?.home.marketTicker) set.add(g.polymarket.home.marketTicker);
    }
    return Array.from(set);
  }, [allGames]);

  useEffect(() => {
    useMarketStore.getState().setOrderbookNeeds('streamrift', orderbookTickers);
    return () => {
      useMarketStore.getState().setOrderbookNeeds('streamrift', []);
    };
  }, [orderbookTickers]);

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-border flex h-12 shrink-0 items-center justify-between border-b px-4">
        <div className="flex items-center gap-3">
          <h1 className="text-foreground text-sm font-bold tracking-wide">
            Eye of StreamRift
          </h1>
          <div className="flex items-center gap-1.5">
            <div
              className={`h-2 w-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`}
            />
            <span className="text-muted-foreground text-xs">
              {connected ? 'Hydra Connected' : 'Disconnected'}
            </span>
          </div>
          <div className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums">
            {allGames.length} games
          </div>
        </div>
      </div>

      {/* Main grid */}
      <div className="grid flex-1 grid-cols-12 gap-px overflow-hidden bg-border"
        style={{ gridTemplateRows: '1fr 280px auto' }}
      >
        {/* Row 2: main panels */}
        {/* cols 1-6: PriceMatrixEnhanced */}
        <div className="col-span-6 flex flex-col overflow-hidden bg-background">
          <div className="flex h-8 shrink-0 items-center border-b border-border/50 px-3">
            <span className="text-muted-foreground text-[10px] font-medium uppercase tracking-wider">
              Price Matrix
            </span>
          </div>
          <div className="flex-1 overflow-hidden">
            <PriceMatrixEnhanced
              games={allGames}
              selectedGameKey={selectedGameKey}
              onSelectGame={setSelectedGameKey}
            />
          </div>
        </div>

        {/* cols 7-9: ArbScanner */}
        <div className="col-span-3 flex flex-col overflow-hidden bg-background">
          <div className="flex h-8 shrink-0 items-center border-b border-border/50 px-3">
            <span className="text-muted-foreground text-[10px] font-medium uppercase tracking-wider">
              Arb Scanner
            </span>
          </div>
          <div className="flex-1 overflow-hidden">
            <ArbScanner games={allGames} />
          </div>
        </div>

        {/* cols 10-12: DefensePosturePanel */}
        <div className="col-span-3 flex flex-col overflow-hidden bg-background">
          <div className="flex h-8 shrink-0 items-center border-b border-border/50 px-3">
            <span className="text-muted-foreground text-[10px] font-medium uppercase tracking-wider">
              Defense Posture
            </span>
          </div>
          <div className="flex-1 overflow-hidden">
            <DefensePosturePanel games={allGames} />
          </div>
        </div>

        {/* Row 3: secondary panels (h-[280px]) */}
        {/* cols 1-4: FeedTapePanel */}
        <div className="col-span-4 flex flex-col overflow-hidden bg-background">
          <div className="flex h-8 shrink-0 items-center border-b border-border/50 px-3">
            <span className="text-muted-foreground text-[10px] font-medium uppercase tracking-wider">
              Feed Tape
            </span>
          </div>
          <div className="flex-1 overflow-hidden">
            <FeedTapePanel />
          </div>
        </div>

        {/* cols 5-8: StalenessMonitor */}
        <div className="col-span-4 flex flex-col overflow-hidden bg-background">
          <div className="flex h-8 shrink-0 items-center border-b border-border/50 px-3">
            <span className="text-muted-foreground text-[10px] font-medium uppercase tracking-wider">
              Staleness Monitor
            </span>
          </div>
          <div className="flex-1 overflow-hidden">
            <StalenessMonitor />
          </div>
        </div>

        {/* cols 9-12: MicrostructurePanel */}
        <div className="col-span-4 flex flex-col overflow-hidden bg-background">
          <div className="flex h-8 shrink-0 items-center border-b border-border/50 px-3">
            <span className="text-muted-foreground text-[10px] font-medium uppercase tracking-wider">
              Microstructure
            </span>
          </div>
          <div className="flex-1 overflow-hidden">
            <MicrostructurePanel selectedGame={selectedGame} />
          </div>
        </div>

        {/* Row 4: BookDepthPanel (conditional) */}
        {selectedGame && (
          <div className="col-span-12 flex flex-col overflow-hidden bg-background">
            <div className="flex-1 overflow-hidden">
              <BookDepthPanel game={selectedGame} onClose={() => setSelectedGameKey(null)} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
